import is from '@sindresorhus/is';
import { useMemo } from 'react';

import type { BreadcrumbItem } from '../../../../ui/source/core/breadcrumbs/Breadcrumbs';
import { useFamiliesWithClusters } from '../api/use-families-with-clusters';
import { useJobRoles } from '../api/use-job-roles';

type UseProfileBreadcrumbsProps = {
    readonly architectureId: string;
    readonly clusterId: string;
    readonly currentItemTitle: string;
    readonly familyId: string;
    readonly parentRoleId?: string | undefined;
};

type UseProfileBreadcrumbsResult = {
    readonly breadcrumbItems: readonly BreadcrumbItem[];
    readonly isLoading: boolean;
};

const createRoleBreadcrumbItem = (
    roleId: string | undefined,
    roles: readonly { readonly id: string; readonly title: string }[] | undefined,
): BreadcrumbItem | null => {
    if (!is.nonEmptyString(roleId) || is.nullOrUndefined(roles)) {
        return null;
    }

    const role = roles.find((r) => r.id === roleId);

    return is.nullOrUndefined(role) ? null : { label: role.title };
};

export const useProfileBreadcrumbs = ({
    architectureId,
    clusterId,
    currentItemTitle,
    familyId,
    parentRoleId,
}: UseProfileBreadcrumbsProps): UseProfileBreadcrumbsResult => {
    const { data: familiesWithClusters, isLoading: isFamiliesLoading } = useFamiliesWithClusters(architectureId);

    const shouldFetchRoles = is.nonEmptyString(clusterId);
    const { data: roles, isLoading: isRolesLoading } = useJobRoles(architectureId, clusterId ?? '', {
        enabled: shouldFetchRoles,
    });

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: will be reduced once we have backend responses
    const breadcrumbItems = useMemo((): readonly BreadcrumbItem[] => {
        const items: BreadcrumbItem[] = [];

        if (!is.nullOrUndefined(familiesWithClusters) && is.nonEmptyString(familyId)) {
            const family = familiesWithClusters.find((f) => f.id === familyId);
            if (!is.nullOrUndefined(family)) {
                items.push({ label: family.title });

                if (is.nonEmptyString(clusterId)) {
                    const cluster = family.clusters.find((c) => c.id === clusterId);
                    if (!is.nullOrUndefined(cluster)) {
                        items.push({ label: cluster.title });
                    }
                }
            }
        }

        const roleItem = createRoleBreadcrumbItem(parentRoleId, roles);
        if (!is.nullOrUndefined(roleItem)) {
            items.push(roleItem);
        }

        items.push({ label: currentItemTitle });

        return items;
    }, [familyId, clusterId, parentRoleId, familiesWithClusters, roles, currentItemTitle]);

    const isLoading = isFamiliesLoading || (shouldFetchRoles && isRolesLoading);

    return {
        breadcrumbItems,
        isLoading,
    };
};
